import React, { useState, useEffect } from 'react';

interface CookiePreferences {
    necessary: boolean;
    functional: boolean;
    marketing: boolean;
    timestamp: number;
}

interface CookieConsentProps {
    isOpen?: boolean;
    onClose?: () => void;
}

// Helper functions for cookie management
const setCookie = (name: string, value: string, days: number) => {
    const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
    // Set cookie with SameSite=Lax for better compatibility and security
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
};

const getCookie = (name: string): string | null => {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === ' ') c = c.substring(1, c.length);
        if (c.indexOf(nameEQ) === 0) return decodeURIComponent(c.substring(nameEQ.length, c.length));
    }
    return null;
};

const CookieConsent: React.FC<CookieConsentProps> = ({ isOpen: externalIsOpen, onClose }) => {
    // viewState: 'hidden' | 'banner' | 'modal'
    const [viewState, setViewState] = useState<'hidden' | 'banner' | 'modal'>('hidden');
    const [preferences, setPreferences] = useState<CookiePreferences>({
        necessary: true,
        functional: false,
        marketing: false,
        timestamp: Date.now()
    });

    useEffect(() => {
        // Check if user has already made a choice using actual cookies
        const savedConsent = getCookie('vanity_cookie_consent');
        if (!savedConsent && externalIsOpen === undefined) {
            // Show banner after 5 seconds delay for better UX
            const timer = setTimeout(() => {
                setViewState('banner');
            }, 5000);
            return () => clearTimeout(timer);
        } else if (savedConsent) {
            try {
                const parsed = JSON.parse(savedConsent);
                setPreferences(parsed);
                // If saved, we stay hidden unless externalIsOpen forces us
            } catch (e) {
                console.error('Failed to parse cookie consent', e);
            }
        }
    }, [externalIsOpen]);

    // Handle external control (from footer icon)
    useEffect(() => {
        if (externalIsOpen === true) {
            setViewState('modal');
        } else if (externalIsOpen === false && viewState === 'modal') {
            setViewState('hidden');
        }
    }, [externalIsOpen]);

    const savePreferences = (prefs: CookiePreferences) => {
        // Save to actual cookies with 1 year expiration (365 days)
        setCookie('vanity_cookie_consent', JSON.stringify(prefs), 365);
        setPreferences(prefs);
        setViewState('hidden');
        if (onClose) onClose();
    };

    const handleAcceptAll = () => {
        savePreferences({
            necessary: true,
            functional: true,
            marketing: true,
            timestamp: Date.now()
        });
    };

    const handleNecessaryOnly = () => {
        savePreferences({
            necessary: true,
            functional: false,
            marketing: false,
            timestamp: Date.now()
        });
    };

    const handleSaveCustom = () => {
        savePreferences({
            ...preferences,
            timestamp: Date.now()
        });
    };

    const openSettings = () => {
        setViewState('modal');
    };

    if (viewState === 'hidden') return null;

    // --- Banner View ---
    if (viewState === 'banner') {
        return (
            <div className="fixed bottom-6 right-6 left-6 md:left-auto md:w-[400px] z-[9999] animate-slideUp">
                <div className="bg-white/95 backdrop-blur-md rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-gray-100 p-6">
                    <div className="flex items-start gap-4 mb-4">
                        <span className="text-2xl">🍪</span>
                        <div>
                            <h3 className="font-serif text-lg font-bold text-vanity-black mb-1">Cookie-Präferenzen</h3>
                            <p className="text-xs text-gray-500 leading-relaxed">
                                Wir nutzen Cookies für ein optimales Erlebnis.
                            </p>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={openSettings}
                            className="flex-1 px-4 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider border border-gray-200 text-gray-600 hover:bg-gray-50 transition-all"
                        >
                            Einstellungen
                        </button>
                        <button
                            onClick={handleAcceptAll}
                            className="flex-1 px-4 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider bg-vanity-black text-white hover:bg-gray-800 transition-all shadow-md"
                        >
                            Akzeptieren
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // --- Modal View ---
    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fadeIn p-4">
            <div className="bg-white w-full max-w-xl rounded-3xl shadow-2xl overflow-hidden animate-zoomIn">
                {/* Header */}
                <div className="bg-gradient-to-r from-vanity-black to-gray-800 p-6 text-white relative">
                    {/* Close button for modal if opened externally or via settings (optional UX, usually accepting/saving is the exit) */}
                    {/* We rely on actions to close, but could add X if needed. Sticking to actions. */}
                    <div className="flex items-center gap-3 mb-2">
                        <span className="text-4xl">🍪</span>
                        <h2 className="font-serif text-2xl font-bold italic">Cookie-Einstellungen</h2>
                    </div>
                    <p className="text-xs text-gray-300">
                        Wir respektieren Ihre Privatsphäre und verwenden Cookies, um Ihre Erfahrung zu verbessern.
                    </p>
                </div>

                {/* Content */}
                <div className="p-6 space-y-5 max-h-[60vh] overflow-y-auto">
                    <p className="text-sm text-gray-700 leading-relaxed">
                        Diese Website verwendet Cookies, um Ihnen die bestmögliche Nutzererfahrung zu bieten.
                        Sie können Ihre Einstellungen jederzeit über das Cookie-Symbol im Footer anpassen.
                    </p>

                    {/* Cookie Categories */}
                    <div className="space-y-4">
                        {/* Necessary */}
                        <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-6 bg-vanity-black rounded-full flex items-center justify-end px-1">
                                        <div className="w-4 h-4 bg-white rounded-full"></div>
                                    </div>
                                    <h3 className="text-sm font-bold text-gray-900">Notwendig</h3>
                                </div>
                                <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Immer aktiv</span>
                            </div>
                            <p className="text-xs text-gray-600 ml-[60px]">
                                Erforderlich für grundlegende Funktionen wie Navigation und Zugriff auf sichere Bereiche.
                            </p>
                        </div>

                        {/* Functional */}
                        <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-3">
                                    <button
                                        onClick={() => setPreferences({ ...preferences, functional: !preferences.functional })}
                                        className={`w-12 h-6 rounded-full flex items-center transition-all ${preferences.functional ? 'bg-vanity-black justify-end' : 'bg-gray-300 justify-start'
                                            } px-1`}
                                    >
                                        <div className="w-4 h-4 bg-white rounded-full"></div>
                                    </button>
                                    <h3 className="text-sm font-bold text-gray-900">Funktional</h3>
                                </div>
                            </div>
                            <p className="text-xs text-gray-600 ml-[60px]">
                                Ermöglichen erweiterte Funktionen wie personalisierte Inhalte und gespeicherte Präferenzen.
                            </p>
                        </div>

                        {/* Marketing */}
                        <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-3">
                                    <button
                                        onClick={() => setPreferences({ ...preferences, marketing: !preferences.marketing })}
                                        className={`w-12 h-6 rounded-full flex items-center transition-all ${preferences.marketing ? 'bg-vanity-black justify-end' : 'bg-gray-300 justify-start'
                                            } px-1`}
                                    >
                                        <div className="w-4 h-4 bg-white rounded-full"></div>
                                    </button>
                                    <h3 className="text-sm font-bold text-gray-900">Marketing & Analyse</h3>
                                </div>
                            </div>
                            <p className="text-xs text-gray-600 ml-[60px]">
                                Helfen uns, die Website-Nutzung zu verstehen und relevante Inhalte anzubieten.
                            </p>
                        </div>
                    </div>

                    {/* Privacy Link */}
                    <div className="pt-4 border-t border-gray-200">
                        <a
                            href="/datenschutz"
                            className="text-sm text-vanity-black hover:underline font-medium"
                        >
                            Mehr über unsere Datenschutzrichtlinien →
                        </a>
                    </div>
                </div>

                {/* Actions */}
                <div className="p-6 bg-gray-50 border-t border-gray-200 flex flex-col md:flex-row gap-3">
                    <button
                        onClick={handleNecessaryOnly}
                        className="flex-1 px-5 py-2.5 rounded-full text-xs font-black uppercase tracking-widest border-2 border-gray-300 text-gray-700 hover:bg-gray-100 transition-all"
                    >
                        Nur Notwendige
                    </button>
                    <button
                        onClick={handleSaveCustom}
                        className="flex-1 px-5 py-2.5 rounded-full text-xs font-black uppercase tracking-widest border-2 border-vanity-black text-vanity-black hover:bg-gray-100 transition-all"
                    >
                        Speichern
                    </button>
                    <button
                        onClick={handleAcceptAll}
                        className="flex-1 px-5 py-2.5 rounded-full text-xs font-black uppercase tracking-widest bg-vanity-black text-white hover:bg-gray-800 transition-all shadow-lg"
                    >
                        Alle Akzeptieren
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CookieConsent;
